import { useEffect, useMemo } from 'react';

import { TrelloCardAri } from '@atlassian/ari';
import { useMemberNodeId } from '@trello/business-logic-react/member';
import { smartScheduledCardsSharedState } from '@trello/business-logic/planner';
import { useSharedStateSelector } from '@trello/shared-state';

import { PLANNER_CONSTANTS } from 'app/src/components/Planner';
import { useTrelloProposedEventsQuery } from './TrelloProposedEventsQuery.generated';
import { useIsSmartScheduleM2Enabled } from './useIsSmartScheduleM2Enabled';

export type ProposedEvent = {
  id: string;
  start: string;
  end: string;
  eventId: string;
  title: string;
  durationEditable: boolean;
  startEditable: boolean;
};

/**
 * Hook to get a member's proposed events generated by the smart schedule LLM.
 * If the persisted proposed events feature gate is enabled, the proposed events are fetched from server.
 * The smart schedule navigation active index is also initialized to 0 if there are persisted proposed
 * events and no event is currently active.
 *
 * If the feature gate is disabled, the proposed events are fetched from the smart schedule shared state.
 *
 * @returns {Object} - An object containing the proposed events array.
 */
export const useProposedEvents = () => {
  const memberNodeId = useMemberNodeId();

  const isSmartScheduleM2Enabled = useIsSmartScheduleM2Enabled();

  const { data } = useTrelloProposedEventsQuery({
    variables: {
      nodeId: memberNodeId,
    },
    waitOn: ['None'],
    fetchPolicy: 'cache-first',
    skip: !isSmartScheduleM2Enabled,
  });

  const sharedStateProposedEvents = useSharedStateSelector(
    smartScheduledCardsSharedState,
    (state) => state.proposedEvents || [],
  );

  const persistedProposedEvents = useMemo(
    () =>
      data?.trello.member?.planner?.proposedEvents?.edges?.map(
        (edge) => edge.node,
      ) ?? [],
    [data],
  );

  const formattedProposedEvents = useMemo(
    () =>
      persistedProposedEvents.reduce<ProposedEvent[]>((acc, event) => {
        if (event && event.startTime && event.endTime) {
          const card = event.cards?.edges?.[0]?.node;
          if (card && card.name) {
            try {
              const cardIdFromEvent = TrelloCardAri.parse(card.id).cardId;

              acc.push({
                id: event.objectId,
                start: event.startTime,
                end: event.endTime,
                eventId:
                  PLANNER_CONSTANTS.PROPOSED_EVENT_ID_PREFIX + cardIdFromEvent,
                title: card.name,
                durationEditable: true,
                startEditable: true,
              });
            } catch {
              return acc;
            }
          }
        }
        return acc;
      }, []),
    [persistedProposedEvents],
  );

  const proposedEvents: ProposedEvent[] = useMemo(() => {
    return isSmartScheduleM2Enabled
      ? formattedProposedEvents
      : sharedStateProposedEvents;
  }, [
    isSmartScheduleM2Enabled,
    formattedProposedEvents,
    sharedStateProposedEvents,
  ]);

  const hasPersistedProposedEvents = useMemo(() => {
    return formattedProposedEvents.length > 0;
  }, [formattedProposedEvents]);

  useEffect(() => {
    if (
      hasPersistedProposedEvents &&
      smartScheduledCardsSharedState.value.activeIndex === null
    ) {
      smartScheduledCardsSharedState.setValue((prev) => ({
        ...prev,
        activeIndex: 0,
      }));
    }
  }, [hasPersistedProposedEvents]);

  return {
    proposedEvents,
  };
};
